/**
 * Accès stable à l'API v2 et chargement des périmètres visibles.
 */
import { useEffect, useRef, type MutableRefObject } from 'react';
import type { MonitoringV2Api, ScopeType, ScopesResponse } from '../../../api/monitoring/v2';
import { useMonitoringV2Api } from '../../../api/monitoring/useMonitoringV2Api';
import { useMonitoringQuery } from './useMonitoringQuery';
import type { MonitoringFilters } from './useMonitoringFilters';

/**
 * L'objet d'API change d'identité à chaque rendu (mémoire projet : hooks
 * d'API non mémoïsés, `apiClients.ts` volontairement non modifié). On le lit
 * par une ref, jamais dans un tableau de dépendances.
 */
export const useMonitoringApiRef = (): MutableRefObject<MonitoringV2Api> => {
    const api = useMonitoringV2Api();
    const ref = useRef(api);
    ref.current = api;
    return ref;
};

/**
 * Charge `/scopes` : périmètres visibles et `today` (jour courant du fuseau
 * métier, contrat v2.2 §11). « Actualiser » (`refreshToken`) le recharge.
 * Indépendant des filtres : la période par défaut en dépend.
 */
export const useScopes = (refreshToken: number) => {
    const apiRef = useMonitoringApiRef();
    return useMonitoringQuery<ScopesResponse>('scopes', (signal) => apiRef.current.getScopes(signal), {
        refreshToken,
    });
};

/**
 * Pour un périmètre typé sans identifiant (lien partiel), présélectionne la
 * première entrée visible.
 */
export const useScopePreselect = (
    data: ScopesResponse | null,
    filters: MonitoringFilters,
    setScope: (type: ScopeType, id?: number) => void
) => {
    useEffect(() => {
        if (!data || filters.scopeType === 'all' || filters.scopeId !== undefined) return;
        const entries =
            filters.scopeType === 'school' ? data.schools : filters.scopeType === 'parent' ? data.parents : data.students;
        if (entries.length) setScope(filters.scopeType, entries[0].id);
    }, [data, filters.scopeType, filters.scopeId, setScope]);
};
